import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import BaseApi from "../../../services/base-api";
import { Role, User, RoleFormData } from "../../../types/Role";

const useToast = () => ({
  success: (msg: string) => console.log("✅ Success:", msg),
  err: (msg: string) => console.error("❌ Error:", msg),
});

export const useRolesModals = (refreshRoles: () => Promise<void>) => {
  const { t } = useTranslation();
  const { success, err } = useToast();

  const toastRef = useRef({ success, err });
  
  useEffect(() => {
    toastRef.current = { success, err };
  }, [success, err]);

  const [modals, setModals] = useState({
    create: false,
    edit: false,
    delete: false,
    permissions: false,
    users: false,
    duplicate: false,
  });

  const [currentRole, setCurrentRole] = useState<Role | null>(null);
  const [saving, setSaving] = useState(false);
  const [permissionSearch, setPermissionSearch] = useState("");
  
  // Users state
  const [roleUsers, setRoleUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Form data state
  const [formData, setFormData] = useState<RoleFormData>({
    name: "",
    displayName: { en: "", ar: "" },
    description: { en: "", ar: "" },
    isActive: true,
    permissions: {},
  });

  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      displayName: { en: "", ar: "" },
      description: { en: "", ar: "" },
      isActive: true,
      permissions: {},
    });
    setPermissionSearch("");
    setRoleUsers([]);
  }, []);

  const closeModal = useCallback(
    (modalName: keyof typeof modals) => {
      setModals((prev) => ({ ...prev, [modalName]: false }));
      setCurrentRole(null);
      resetForm();
    },
    [resetForm]
  );

  // Update form data
  const updateFormData = useCallback((updates: Partial<RoleFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  // Fetch role by ID
  const fetchRoleById = useCallback(
    async (id: string) => {
      try {
        const res = await BaseApi.get(`/roles/${id}`);
        if (res?.success) {
          return res.data;
        }
      } catch (error) {
        console.error("Error fetching role:", error);
        toastRef.current.err(t("Failed to fetch role details"));
      }
      return null;
    },
    [t]
  );

  // Fetch users with this role
  const fetchRoleUsers = useCallback(
    async (roleId: string) => {
      try {
        setLoadingUsers(true);
        const res = await BaseApi.get(`/roles/${roleId}/users`);
        if (res?.success && Array.isArray(res.data)) {
          setRoleUsers(res.data);
        } else {
          setRoleUsers([]);
        }
      } catch (error) {
        console.error("Error fetching role users:", error);
        toastRef.current.err(t("Failed to fetch users"));
        setRoleUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    },
    [t]
  );

  // ==================== OPEN MODALS ====================

  const openCreateModal = useCallback(() => {
    resetForm();
    setModals((prev) => ({ ...prev, create: true }));
  }, [resetForm]);

  const openEditModal = useCallback(
    async (role: Role) => {
      if (!role?.id) {
        console.error("Invalid role ID");
        return;
      }

      const roleDetails = await fetchRoleById(role.id);
      if (!roleDetails) {
        console.error("Failed to fetch role details");
        toastRef.current.err(t("Failed to load role details"));
        return;
      }

      setCurrentRole(roleDetails);
      
      // ✅ Add safer description parsing
      let descEn = "";
      let descAr = "";
      try {
        if (typeof roleDetails.description === "string") {
          descEn = roleDetails.description;
        } else if (roleDetails.description && typeof roleDetails.description === "object") {
          descEn = roleDetails.description.en || "";
          descAr = roleDetails.description.ar || "";
        }
      } catch (error) {
        console.error("Error parsing description:", error);
      }

      setFormData({
        name: roleDetails.name || "",
        displayName: {
          en: roleDetails.displayName?.en || "",
          ar: roleDetails.displayName?.ar || "",
        },
        description: { en: descEn, ar: descAr },
        permissions: roleDetails.permissions || {},
        isActive: roleDetails.isActive ?? true,
      });
      
      setModals((prev) => ({ ...prev, edit: true }));
    },
    [fetchRoleById, t]
  );

  const openPermissionsModal = useCallback(
    async (role: Role) => {
      if (!role?.id) return;

      const roleDetails = await fetchRoleById(role.id);
      if (roleDetails) {
        setCurrentRole(roleDetails);
        setFormData((prev) => ({
          ...prev,
          permissions: roleDetails.permissions || {},
        }));
        setModals((prev) => ({ ...prev, permissions: true }));
      }
    },
    [fetchRoleById]
  );

  const openUsersModal = useCallback(
    async (role: Role) => {
      if (!role?.id) return;
      setCurrentRole(role);
      setModals((prev) => ({ ...prev, users: true }));
      await fetchRoleUsers(role.id);
    },
    [fetchRoleUsers]
  );

  const openDeleteModal = useCallback((role: Role) => {
    if (!role?.id) return;
    setCurrentRole(role);
    setModals((prev) => ({ ...prev, delete: true }));
  }, []);

  const openDuplicateModal = useCallback((role: Role) => {
    if (!role?.id) return;
    setCurrentRole(role);
    setModals((prev) => ({ ...prev, duplicate: true }));
  }, []);

  // ==================== CRUD OPERATIONS ====================

  const handleCreate = useCallback(async () => {
    if (!formData.name.trim()) {
      toastRef.current.err(t("Role name is required"));
      return;
    }

    try {
      setSaving(true);
      const res = await BaseApi.post("/roles", {
        name: formData.name.trim(),
        displayName: formData.displayName,
        description: formData.description,
        isActive: formData.isActive,
        permissions: formData.permissions,
      });

      if (res?.success) {
        toastRef.current.success(t("Role created successfully!"));
        await refreshRoles();
        closeModal("create");
      }
    } catch (error) {
      console.error("Error creating role:", error);
      toastRef.current.err(t("Failed to create role"));
    } finally {
      setSaving(false);
    }
  }, [formData, refreshRoles, closeModal, t]);

  const handleUpdate = useCallback(async () => {
    if (!currentRole?.id || !formData.name.trim()) {
      toastRef.current.err(t("Role name is required"));
      return;
    }

    try {
      setSaving(true);
      const res = await BaseApi.put(`/roles/${currentRole.id}`, {
        name: formData.name.trim(),
        displayName: formData.displayName,
        description: formData.description,
        isActive: formData.isActive,
        permissions: formData.permissions,
      });

      if (res?.success) {
        toastRef.current.success(t("Role updated successfully!"));
        await refreshRoles();
        closeModal("edit");
      }
    } catch (error) {
      console.error("Error updating role:", error);
      toastRef.current.err(t("Failed to update role"));
    } finally {
      setSaving(false);
    }
  }, [currentRole, formData, refreshRoles, closeModal, t]);

  const handleDelete = useCallback(async () => {
    if (!currentRole?.id) return;

    try {
      setSaving(true);
      const res = await BaseApi.delete(`/roles/${currentRole.id}`);

      if (res?.success) {
        toastRef.current.success(t("Role deleted successfully!"));
        await refreshRoles();
        closeModal("delete");
      }
    } catch (error) {
      console.error("Error deleting role:", error);
      toastRef.current.err(t("Failed to delete role"));
    } finally {
      setSaving(false);
    }
  }, [currentRole, refreshRoles, closeModal, t]);

  const handleUpdatePermissions = useCallback(async () => {
    if (!currentRole?.id) return;

    try {
      setSaving(true);
      const res = await BaseApi.patch(`/roles/${currentRole.id}/permissions`, {
        permissions: formData.permissions,
      });

      if (res?.success) {
        toastRef.current.success(t("Permissions updated successfully!"));
        await refreshRoles();
        closeModal("permissions");
      }
    } catch (error) {
      console.error("Error updating permissions:", error);
      toastRef.current.err(t("Failed to update permissions"));
    } finally {
      setSaving(false);
    }
  }, [currentRole, formData.permissions, refreshRoles, closeModal, t]);

  const handleDuplicate = useCallback(async () => {
    if (!currentRole?.id) return;

    try {
      setSaving(true);
      const res = await BaseApi.post(`/roles/duplicate/${currentRole.id}`, {});

      if (res?.success) {
        toastRef.current.success(t("Role duplicated successfully!"));
        await refreshRoles();
        closeModal("duplicate");
      }
    } catch (error) {
      console.error("Error duplicating role:", error);
      toastRef.current.err(t("Failed to duplicate role"));
    } finally {
      setSaving(false);
    }
  }, [currentRole, refreshRoles, closeModal, t]);

  // ==================== PERMISSION TOGGLES ====================

  const toggleModulePermission = useCallback((module: string, action: string) => {
    setFormData((prev) => {
      const modulePerms = prev.permissions?.[module] || [];
      const hasAction = modulePerms.includes(action);

      return {
        ...prev,
        permissions: {
          ...(prev.permissions || {}),
          [module]: hasAction
            ? modulePerms.filter((a) => a !== action)
            : [...modulePerms, action],
        },
      };
    });
  }, []);

  const toggleAllModulePermissions = useCallback((module: string, actions: string[]) => {
    if (!actions || !Array.isArray(actions)) return;
    
    setFormData((prev) => {
      const modulePerms = prev.permissions?.[module] || [];
      const allSelected = actions.every((action) => modulePerms.includes(action));

      return {
        ...prev,
        permissions: {
          ...(prev.permissions || {}),
          [module]: allSelected ? [] : [...actions],
        },
      };
    });
  }, []);

  const selectAllPermissions = useCallback((allModules: { [key: string]: string[] }) => {
    setFormData((prev) => ({
      ...prev,
      permissions: { ...allModules },
    }));
  }, []);

  const clearAllPermissions = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      permissions: {},
    }));
  }, []);

  return {
    modals,
    currentRole,
    formData,
    saving,
    permissionSearch,
    setPermissionSearch,
    roleUsers,
    loadingUsers,
    updateFormData,
    openCreateModal,
    openEditModal,
    openPermissionsModal,
    openUsersModal,
    openDeleteModal,
    openDuplicateModal,
    closeModal,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleUpdatePermissions,
    handleDuplicate,
    toggleModulePermission,
    toggleAllModulePermissions,
    selectAllPermissions,
    clearAllPermissions,
  };
};